<template>
  <tbody>
    <tr>
      <td>
        {{ record.lastUpdateDate | formatDateMMDDYYYY }}
      </td>
      <td style="font-weight:bold">
        <span
          v-if="fieldEnabled('FIRST_NAME')
          && fieldEnabled('LAST_NAME')"
        >
          {{ this.record.lastName+', '+ this.record.firstName }}
          <span
            v-if="this.record.middleName
              && fieldEnabled('MIDDLE_NAME')"
          >
            {{ this.record.middleName }}
          </span>
        </span>
      </td>
      <td v-if="fieldEnabled('DATE_OF_BIRTH')">
        {{ displayDOBFormat(record.dateOfBirth) }}
      </td>
      <td v-if="fieldEnabled('GENDER_CODE')">
        <span v-if="record.genderCode">
          {{ mapGender(record.genderCode) }}
        </span>
      </td>
      <td v-if="showEthnicityOrRace">
        <div v-if="showEthnicity">
          {{ ethnicityIndicatorDescription }}
        </div>
        <div v-if="showRace">
          {{ getRaces() }}
        </div>
      </td>
      <td v-if="fieldEnabled('SOCIAL_SECURITY_NUMBER')">
        <div v-if="hasModule('PRSID:PRPRF:SSN:VIEW')">
          {{ record.socialSecurityNumber | BlankPlaceHolder}}
        </div>
      </td>
      <td
        v-if="fieldEnabled('SCHOOL_CODE')
          || fieldEnabled('DISTRICT_CODE')"
      >
        <div>
          <div v-if="fieldEnabled('DISTRICT_CODE')">
            <router-link
              :to="{
                name: 'PIDistrictInfoDetails',
                params: {
                  code: record.districtCode
                }
              }"
              class="has-text-weight-bold has-text-primary"
              style="font-size:14px"
            >
              {{ record.districtName }}
            </router-link>
            <span
              class="has-text-grey"
              style="padding-left: 0.25rem"
            >
              ({{ record.districtCode }})
            </span>
          </div>
          <div v-if="fieldEnabled('SCHOOL_CODE')">
            <div>
              <router-link
                :to="{
                  name: 'PISchoolInfoDetails',
                  params: {
                    districtCode: record.districtCode,
                    schoolCode: record.schoolCode,
                  },
                }"
                class="is-italic"
              >
                {{ record.schoolName }}
              </router-link>
              <span
                class="has-text-grey is-italic"
                style="padding-left: 0.25rem"
              >
                ({{ record.schoolCode }})
              </span>
            </div>
          </div>
        </div>
      </td>
      <td style="padding-left: 30px">
        <div class="list-content">
          <router-link
            v-if="this.activeModule.name === 'personId'"
            :to="{
              name: 'PidHistoryRecordDetails',
              query: {
                recordId: this.record.lastTranSerialNumber,
                retired: isRetired,
              }
            }"
          >
            <font-awesome-icon
              :icon="['far', 'angle-double-right']"
              size="2x"
              title="View Details"
            />
          </router-link>
          <router-link
            v-if="this.activeModule.name === 'directMatch'"
            :to="{
              name: 'DmHistoryRecordDetails',
              query: {
                recordId: this.record.lastTranSerialNumber,
                retired: isRetired,
              }
            }"
          >
            <font-awesome-icon
              :icon="['far', 'angle-double-right']"
              size="2x"
              title="View Details"
            />
          </router-link>
        </div>
      </td>
    </tr>
  </tbody>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleRight,
} from '@fortawesome/pro-regular-svg-icons';
import { mapActions, mapGetters } from 'vuex';
import FieldLabels from '@/mixins/FieldLabels';
import LookupsUtils from '@/mixins/LookupsUtils';
import DateUtils from '@/mixins/DateUtils';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faAngleDoubleRight,
);

export default {
  mixins: [
    FieldLabels,
    PermissionManager,
    LookupsUtils,
    DateUtils,
  ],
  props: {
    isRetired: {
      type: Boolean,
      default: false,
    },
    record: {
      type: Object,
      required: true,
    },
    person: {
      type: Object,
      required: true,
    },
    enabledRaces: {
      type: Array,
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      activeModule: 'appModules/getActiveModule',
    }),
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    ...mapGetters('lookups', [
      'activeGenders',
      'activeRaces',
      'activeEthnicities',
    ]),
    ...mapGetters('fieldLabelsModule', [
      'getFieldEnabledForPersonType',
    ]),
    ethnicityIndicatorDescription() {
      if (this.record.ethnicityIndicator) {
        return this.mapEthnicity(this.record.ethnicityIndicator);
      }
      return '--';
    },
    showEthnicityOrRace() {
      return this.fieldEnabled('ETHNICITY_INDICATOR') || this.fieldEnabled('RACE_ETHNIC_CODE');
    },
    showEthnicity() {
      return this.fieldEnabled('ETHNICITY_INDICATOR');
    },
    showRace() {
      return this.fieldEnabled('RACE_ETHNIC_CODE');
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadGenders',
      'loadRaces',
      'loadEthnicities',
    ]),
    fieldEnabled(code) {
      return this.getFieldEnabledForPersonType(code, 'ASSIGN', this.person.personType);
    },
    mapRace(race) {
      return this.getDescriptionFromCode(race, this.activeRaces, race);
    },
    mapEthnicity(ethnicity) {
      return this.getDescriptionFromCode(ethnicity, this.activeEthnicities, ethnicity);
    },
    mapGender(code) {
      return this.getDescriptionFromCode(code, this.activeGenders, code);
    },
    raceIsEnabled(fieldName) {
      const field = this.enabledRaces.find(er => er === fieldName);
      return field != null;
    },
    getRaces() {
      const enabledRaceValues = [];
      if (this.record.raceEthnicCode && this.raceIsEnabled('RACE_ETHNIC_CODE')) {
        enabledRaceValues.push(this.mapRace(this.record.raceEthnicCode));
      }
      if (this.record.race2Code && this.raceIsEnabled('RACE2_CODE')) {
        enabledRaceValues.push(this.mapRace(this.record.race2Code));
      }
      if (this.record.race3Code && this.raceIsEnabled('RACE3_CODE')) {
        enabledRaceValues.push(this.mapRace(this.record.race3Code));
      }
      if (this.record.race4Code && this.raceIsEnabled('RACE4_CODE')) {
        enabledRaceValues.push(this.mapRace(this.record.race4Code));
      }
      if (this.record.race5Code && this.raceIsEnabled('RACE5_CODE')) {
        enabledRaceValues.push(this.mapRace(this.record.race5Code));
      }
      return enabledRaceValues.length > 0 ? enabledRaceValues.toString() : '--';
    },
  },
};
</script>

<style lang="sass" scoped>
</style>
